import React, { useCallback, useState } from "react";
import {
  Popover,
  Box,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Button,
  Dialog,
} from "@mui/material";
import moment from "moment";
import clsx from "clsx";
import ModalCloseButton from "../../components/common/commonModal/modalCloseButton";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { readNotification } from "../../services/dashBoardServices";
import {
  getCustomerByIDAPI,
  getEventByIDAPI,
} from "../../services/EnquiriesServices/EnquiriesServices";
import {
  breadStatus,
  changeStatusOfInnerCompoenent,
} from "../../redux/slices/enquirySlice";

interface NotificationPopoverProps {
  anchorEl: null | HTMLElement;
  onClose: () => void;
  notifications: any;
  handleScroll: any;
  setUnreadCount?: React.Dispatch<React.SetStateAction<any>>; // Add this prop
}

const VIEWABLE_NOTIFICATION_TYPES = [
  "user",
  "venue",
  "supplier",
  "customer",
  "enquiry",
];

const formatTime = (timestamp: string) => moment(timestamp).fromNow();

const extractIdFromLink = (link?: string) => {
  if (!link) return "";

  try {
    const url = new URL(link);
    const queryId = Array.from(url.searchParams.values())[0];
    if (queryId) return queryId;

    const segments = url.pathname.split("/").filter(Boolean);
    return segments[segments.length - 1] || "";
  } catch {
    if (link.includes("=")) {
      return link.split("=").pop()?.split("&")[0] || "";
    }
    const segments = link.split("/").filter(Boolean);
    return segments[segments.length - 1] || "";
  }
};

const NotificationPopover: React.FC<NotificationPopoverProps> = ({
  anchorEl,
  onClose,
  notifications,
  handleScroll,
  setUnreadCount,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = React.useState("1");
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const open = Boolean(anchorEl);
  // console.log(anchorEl,"notifyAnchorEl>>>>");
  // console.log(notifications,"notifications>>>>");
  // Normalize notifications data structure
  const normalizeNotifications = (data: any) => {
    if (!data) return [];
    // If data is the full response object with count/unreadCount/notifications
    if (data.notifications) {
      // Handle case where notifications is an array of arrays
      if (Array.isArray(data.notifications) && data.notifications.length > 0) {
        // If first item is an array (nested), use that as the main array
        if (Array.isArray(data.notifications[0])) {
          return data.notifications[0]; // Just take the first array
        }
        return data.notifications;
      }
      return [];
    }

    // If data is just an array (raw notifications array)
    if (Array.isArray(data)) {
      // If first item is an array (nested), use that
      if (data.length > 0 && Array.isArray(data[0])) {
        return data[0]; // Just take the first array
      }
      return data;
    }
    // If single notification
    if (data._id) {
      return [data];
    }
    return [];
  };

  // Get all notifications (normalized)
  const allNotifications = normalizeNotifications(notifications);
  const unreadNotifications =
    allNotifications && allNotifications.filter((n: any) => !n.isRead);
  const displayedList =
    tabValue === "1" ? allNotifications : unreadNotifications;
  const handleReadNotification = useCallback(
    async (id: any) => {
      try {
        let payload = {
          isRead: true,
        };
        const response = await readNotification(id, payload);
        // Update the local state with the response data
        if (setUnreadCount && response.data) {
          setUnreadCount((prev: any) => {
            // Create a map to deduplicate notifications by _id
            const notificationMap = new Map();

            // Add all existing notifications to the map
            prev.notifications.forEach((n: any) =>
              notificationMap.set(n._id, n),
            );

            // Update the specific notification
            notificationMap.set(response.data._id, {
              ...(notificationMap.get(response.data._id) || {}),
              ...response.data,
              isRead: true,
            });

            return {
              count: response.count ?? prev.count,
              unreadCount: response.unreadCount ?? prev.unreadCount,
              notifications: Array.from(notificationMap.values()),
            };
          });
        }
        //  dispatch(setNotificationStatus(true))
      } catch (err) {
        console.error(err);
      }
    },
    [setUnreadCount],
  );

  const handleNotificationClick = (item: any) => {
    setSelectedNotification(item);
    setIsDetailOpen(true);
    onClose();
    handleReadNotification(item._id);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedNotification(null);
  };

  const openEnquiryView = (customerId: string, eventId?: string) => {
    dispatch(breadStatus(false));
    dispatch(changeStatusOfInnerCompoenent(false));
    navigate("/enquiries-list/enquiry", {
      state: { EnquiryId: customerId, eventId, replace: true },
    });
    Cookies.set("enquiryId", customerId, { expires: 1 });
    if (eventId) {
      Cookies.set("eventId", eventId, { expires: 1 });
    }
  };

  const handleViewNotification = async () => {
    if (!selectedNotification) return;

    const extractedId = extractIdFromLink(selectedNotification.link);
    console.log(`Notification Extracted id is : ${extractedId}`);

    const notificationType = String(selectedNotification.type || "")
      .trim()
      .toLowerCase();

    if (!extractedId) return;

    if (notificationType === "user") {
      handleCloseDetail();
      navigate("/user-list/view-user", {
        state: { userId: extractedId },
      });
      return;
    }

    if (notificationType === "venue") {
      handleCloseDetail();
      navigate("/venue-list/view-venue", {
        state: { venueId: extractedId },
      });
      return;
    }

    if (notificationType === "supplier") {
      handleCloseDetail();
      navigate("/supplier-list/view-supplier", {
        state: { supplierId: extractedId },
      });
      return;
    }

    if (notificationType === "customer") {
      handleCloseDetail();
      navigate("/customer-list/view-customer", {
        state: { customerId: extractedId },
      });
      return;
    }

    if (notificationType === "enquiry") {
      handleCloseDetail();
      try {
        const eventResponse = await getEventByIDAPI(extractedId);
        const customerFromEvent =
          eventResponse?.data?.customerId?._id ||
          eventResponse?.data?.customerId;

        if (customerFromEvent) {
          openEnquiryView(customerFromEvent, extractedId);
          return;
        }
      } catch (err) {
        console.error("Notification link is not an event view id:", err);
      }

      try {
        const customerResponse = await getCustomerByIDAPI(extractedId);
        const eventFromCustomer =
          customerResponse?.data?.events?.[0]?.eventId?._id ||
          customerResponse?.data?.events?.[0]?.eventId;
        openEnquiryView(extractedId, eventFromCustomer);
      } catch (err) {
        console.error("Failed to open enquiry from notification:", err);
      }
    }
  };

  const detailRows = selectedNotification
    ? [
        { label: "Title", value: selectedNotification.title },
        { label: "Name", value: selectedNotification.name },
        { label: "Message", value: selectedNotification.message },
        { label: "Type", value: selectedNotification.type },
        selectedNotification.clientName
          ? { label: "Client", value: selectedNotification.clientName }
          : null,
        {
          label: "Time",
          value: selectedNotification.createdAt
            ? moment(selectedNotification.createdAt).format(
                "DD MMM YYYY, hh:mm A",
              )
            : "",
        },
      ].filter((row) => row && row.value)
    : [];

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          className:
            "w-[360px] max-h-[80vh] overflow-y-auto shadow-xl rounded-lg",
        }}
      >
        <Box className="p-4 bg-white">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium !text-[0.95rem] text-[#151B2D]">
              Notification
            </p>
          </div>
          <Divider />

          <Tabs
            value={tabValue}
            onChange={(_, val) => setTabValue(val)}
            variant="fullWidth"
            className="mt-2"
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                fontFamily: "Lexend Deca",
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                minHeight: 36,
                color: "#686868",
              },
              "& .Mui-selected": {
                color: "#3983DD !important",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#3983DD",
              },
            }}
          >
            <Tab label={`All (${allNotifications.length})`} value="1" />
            <Tab label={`Unread (${unreadNotifications.length})`} value="2" />
          </Tabs>

          <div
            className="mt-2 max-h-[60vh] overflow-y-auto scrollbar-hidden"
            onScroll={handleScroll}
          >
            {displayedList.length > 0 ? (
              [...displayedList]
                .sort(
                  (a: any, b: any) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime(),
                )
                .map((item: any) => (
                  <div
                    key={item._id}
                    className="flex justify-between items-start gap-3 p-3 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => handleNotificationClick(item)}
                  >
                    <div className="flex gap-3 min-w-0 flex-1">
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.8rem",
                          bgcolor: "#E8EEF5",
                          color: "#3983DD",
                          fontFamily: "Lexend Deca",
                        }}
                      >
                        {item.title?.charAt(0) || "N"}
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-[0.8rem] font-medium text-[#151B2D] leading-5">
                          {item?.title}
                        </p>
                        <p className="text-[0.7rem] text-[#686868] leading-4 truncate">
                          {item?.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 pt-0.5">
                      <span className="text-[0.65rem] text-[#9CA3AF] whitespace-nowrap">
                        {formatTime(item.createdAt)}
                      </span>
                      <span
                        className={clsx(
                          "w-2 h-2 rounded-full shrink-0",
                          item.isRead ? "bg-gray-300" : "bg-[#3983DD]",
                        )}
                      ></span>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center py-6 text-[0.75rem] text-gray-400">
                No {tabValue === "2" ? "unread " : ""}notifications
              </div>
            )}
          </div>
        </Box>
      </Popover>

      <Dialog
        open={isDetailOpen}
        onClose={handleCloseDetail}
        PaperProps={{
          sx: {
            width: 360,
            maxWidth: "calc(100vw - 32px)",
            m: 2,
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <div className="flex items-center justify-between bg-[#3983DD1A] py-2.5 px-4">
          <h1 className="text-[#3983DD] text-[0.85rem] font-[500]">
            Notification Details
          </h1>
          <ModalCloseButton onClick={handleCloseDetail} />
        </div>
        <div className="px-4 py-3 grid grid-cols-[72px_1fr] gap-x-2 gap-y-2">
          {detailRows.map((row: any) => (
            <React.Fragment key={row.label}>
              <p className="text-[0.75rem] text-[#686868] leading-5">
                {row.label}
              </p>
              <p className="text-[0.8rem] text-[#151B2D] font-medium leading-5 break-words">
                {row.value}
              </p>
            </React.Fragment>
          ))}
        </div>
        {VIEWABLE_NOTIFICATION_TYPES.includes(
          String(selectedNotification?.type || "")
            .trim()
            .toLowerCase(),
        ) && (
        <div className="flex justify-end px-4 pr-8 pb-3.5">
          <Button
            variant="contained"
            onClick={handleViewNotification}
            sx={{
              fontFamily: "Lexend Deca",
              fontSize: "0.75rem",
              textTransform: "none",
              borderRadius: "5px",
              minWidth: 72,
              height: 30,
              px: 1.5,
              backgroundColor: "#3983DD",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#2B72F8", boxShadow: "none" },
            }}
          >
            View
          </Button>
        </div>
        )}
      </Dialog>
    </>
  );
};

export default NotificationPopover;
